import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp, FileText, Calendar, Building2, User, Trash2, Download,
  Eye, Search, X, AlertTriangle, CheckCircle2,
  BarChart3, FlaskConical, SortAsc, SortDesc, Database, HardDrive,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useSession } from '@/context/SessionContext';
import { hasPhpBackend, apiListCustomers, apiMyAssessments, apiDeleteReport } from '@/lib/phpApi';
import { generateTrendReportPDF } from '@/lib/pdfGenerator';

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface TrendReport {
  id: string;
  reportNumber: string;
  sampleName: string;
  customerId: number | null;
  customerName: string;
  customerCompany: string;
  standard: string;
  date: string;
  params?: Record<string, number>;
  dosingConfig?: any[];
  result?: Record<string, any>;
  fromServer: boolean;
}

/* ─── PDF Preview Modal ──────────────────────────────────────────────────── */
function PDFPreviewModal({ url, title, onClose, onDownload }: {
  url: string; title: string; onClose: () => void; onDownload: () => void;
}) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-rb-surface rounded-2xl w-full max-w-5xl flex flex-col shadow-2xl overflow-hidden"
          style={{ height: '92vh' }}
        >
          <div className="flex items-center justify-between px-4 py-3 bg-rb-surface border-b border-rb-brand/20 shrink-0">
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-rb-brand" />
              <span className="text-sm font-bold text-white truncate max-w-[340px]">{title}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-rb-brand/15 text-rb-brand border border-rb-brand/25 font-bold uppercase tracking-wider">
                Regulatory PDF
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-rb-brand text-slate-200 hover:bg-yellow-400 transition-colors">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
              <button onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <iframe src={url} className="flex-1 w-full bg-rb-surface" title="PDF Preview" style={{ border: 'none' }} />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─── Component ──────────────────────────────────────────────────────────── */
export default function TrendReportsPage() {
  const { isAuthenticated, user } = useAuth();
  const { activeStandard } = useSession();
  const [customers, setCustomers] = useState<any[]>([]);
  const [activeCustomerId, setActiveCustomerId] = useState<number | 'all'>('all');
  const [reports, setReports] = useState<TrendReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewDownloader, setPreviewDownloader] = useState<() => void>(() => () => {});

  useEffect(() => {
    if (!isAuthenticated || !hasPhpBackend()) {
      setCustomers(JSON.parse(localStorage.getItem('rb_customers') || '[]'));
      return;
    }
    apiListCustomers().then((rows) => {
      setCustomers((rows || []).map((c: any) => ({
        id: c.id, name: c.name, company: c.company,
      })));
    }).catch(() => setCustomers(JSON.parse(localStorage.getItem('rb_customers') || '[]')));
  }, [isAuthenticated]);

  useEffect(() => {
    setLoading(true);
    const currentUserId = user ? Number(user.id) : 0;
    const byUser = (items: any[]) =>
      currentUserId ? items.filter(i => !i.userId || Number(i.userId) === currentUserId) : items;

    const localCustomers = JSON.parse(localStorage.getItem('rb_customers') || '[]');
    const localReports: TrendReport[] = [];

    byUser(JSON.parse(localStorage.getItem('rb_local_assessments') || '[]')).forEach((a: any) => {
      const cust = localCustomers.find((c: any) => c.id === a.customerId);
      localReports.push({
        id: `local_${a.id || a.reportNumber}`,
        reportNumber: a.reportNumber || `RB-LOCAL-${a.id}`,
        sampleName: a.sampleName || 'Untitled',
        customerId: a.customerId || null,
        customerName: cust?.name || 'Unassigned',
        customerCompany: cust?.company || '',
        standard: a.standard || 'UK_EA',
        date: a.createdAt || a.date || new Date().toISOString(),
        params: typeof a.params === 'string' ? JSON.parse(a.params) : a.params,
        dosingConfig: a.dosingConfig || [],
        result: typeof a.result === 'string' ? JSON.parse(a.result) : a.result,
        fromServer: false,
      });
    });

    if (isAuthenticated && hasPhpBackend()) {
      apiMyAssessments().then((data) => {
        const serverRows = (Array.isArray(data) ? data : []).map((a: any) => {
          const cust = localCustomers.find((c: any) => c.id === a.customer_id);
          return {
            id: `srv_${a.id}`,
            reportNumber: a.report_number || `RB-${a.id}`,
            sampleName: a.sample_name || 'Untitled',
            customerId: a.customer_id || null,
            customerName: cust?.name || a.customer_name || 'Unassigned',
            customerCompany: cust?.company || '',
            standard: a.standard || 'UK_EA',
            date: a.created_at || new Date().toISOString(),
            params: typeof a.params === 'string' ? JSON.parse(a.params) : a.params,
            dosingConfig: typeof a.dosing_config === 'string' ? JSON.parse(a.dosing_config) : a.dosing_config,
            result: typeof a.result === 'string' ? JSON.parse(a.result) : a.result,
            fromServer: true,
          };
        });
        setReports([...serverRows, ...localReports].sort((a, b) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()));
      }).catch(() => {
        setReports(localReports.sort((a, b) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()));
      }).finally(() => setLoading(false));
    } else {
      setReports(localReports.sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()));
      setLoading(false);
    }
  }, [isAuthenticated]);

  const filteredReports = useMemo(() => {
    let list = activeCustomerId === 'all'
      ? reports
      : reports.filter(r => r.customerId === activeCustomerId);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.reportNumber.toLowerCase().includes(q) ||
        r.sampleName.toLowerCase().includes(q) ||
        r.customerName.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => {
      const ta = new Date(a.date).getTime();
      const tb = new Date(b.date).getTime();
      return sortOrder === 'newest' ? tb - ta : ta - tb;
    });
  }, [reports, activeCustomerId, search, sortOrder]);

  const formatDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return d; }
  };

  const getComplianceStatus = (result?: Record<string, any>) => {
    if (!result) return { label: 'Pending', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' };
    const summary = result.complianceSummary;
    if (!summary) return { label: 'Pending', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' };
    const ok = summary.compliant === true || summary.overall === 'Compliant';
    if (ok) return { label: 'Compliant', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' };
    return { label: 'Review', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' };
  };

  const buildPDFData = (report: TrendReport) => ({
    id: report.id,
    reportNumber: report.reportNumber,
    sampleName: report.sampleName,
    customerName: report.customerName,
    customerCompany: report.customerCompany,
    standard: report.standard,
    date: new Date(report.date),
    preparerName: user?.name || user?.username || 'Reynolds & Bauhm',
    preparerCompany: (user as any)?.companyName || 'Reynolds & Bauhm',
    params: report.params,
    dosingConfig: report.dosingConfig,
    result: report.result,
  });

  const handlePDF = (report: TrendReport) => {
    try {
      generateTrendReportPDF(buildPDFData(report), 'save');
      toast.success(`PDF downloaded: ${report.reportNumber}`);
    } catch (err: any) {
      toast.error(`PDF failed: ${err.message}`);
    }
  };

  const handlePreview = (report: TrendReport) => {
    try {
      const url = generateTrendReportPDF(buildPDFData(report), 'preview');
      if (!url) { toast.error('Preview failed'); return; }
      setPreviewUrl(url);
      setPreviewTitle(`${report.reportNumber} — ${report.sampleName}`);
      setPreviewDownloader(() => () => {
        generateTrendReportPDF(buildPDFData(report), 'save');
        toast.success(`Downloaded: ${report.reportNumber}`);
      });
    } catch (err: any) {
      toast.error(`Preview failed: ${err.message}`);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const handleDelete = async (id: string) => {
    if (id.startsWith('srv_')) {
      try {
        await apiDeleteReport(parseInt(id.replace('srv_', '')));
        toast.success('Report deleted from server');
      } catch (err: any) {
        toast.error(err.message || 'Delete failed');
      }
    }
    setReports(prev => prev.filter(r => r.id !== id));
    const local = JSON.parse(localStorage.getItem('rb_local_assessments') || '[]');
    localStorage.setItem('rb_local_assessments', JSON.stringify(
      local.filter((a: any) => `local_${a.id || a.reportNumber}` !== id),
    ));
    setDeleteConfirm(null);
  };

  const customersWithReports = useMemo(() => {
    return customers.filter(c => reports.some(r => r.customerId === c.id));
  }, [customers, reports]);

  const serverCount = reports.filter(r => r.fromServer).length;
  const localCount  = reports.filter(r => !r.fromServer).length;

  return (
    <div className="space-y-4">
      {/* Preview modal */}
      {previewUrl && (
        <PDFPreviewModal
          url={previewUrl}
          title={previewTitle}
          onClose={closePreview}
          onDownload={previewDownloader}
        />
      )}

      <PageHeader
        icon={BarChart3}
        title="Assessment Reports"
        subtitle="Saved assessment reports and trend analysis documents"
        badge={
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/15 font-medium">
              <Database className="w-3 h-3" /> {serverCount} Server
            </span>
            <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/15 font-medium">
              <HardDrive className="w-3 h-3" /> {localCount} Local
            </span>
            <span className="text-xs text-slate-500">{reports.length} total</span>
          </div>
        }
      />

      {/* ── Customer tabs ── */}
      {customersWithReports.length > 0 && (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
          <button
            onClick={() => setActiveCustomerId('all')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap shrink-0 ${
              activeCustomerId === 'all'
                ? 'bg-rb-brand/15 text-rb-brand border border-rb-brand/30'
                : 'bg-slate-800/50 text-slate-400 border border-slate-800/40 hover:bg-slate-800 hover:text-slate-200'
            }`}
          >
            <Building2 className="w-3 h-3" /> All
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeCustomerId === 'all' ? 'bg-rb-brand/20 text-rb-brand' : 'bg-slate-700 text-slate-500'}`}>
              {reports.length}
            </span>
          </button>
          {customersWithReports.map(c => {
            const count = reports.filter(r => r.customerId === c.id).length;
            const active = activeCustomerId === c.id;
            return (
              <button key={c.id} onClick={() => setActiveCustomerId(c.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap shrink-0 ${
                  active
                    ? 'bg-rb-brand/15 text-rb-brand border border-rb-brand/30'
                    : 'bg-slate-800/50 text-slate-400 border border-slate-800/40 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                <User className="w-3 h-3" />
                <span className="truncate max-w-[120px]">{c.name}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-rb-brand/20 text-rb-brand' : 'bg-slate-700 text-slate-500'}`}>
                  {count}
                </span>
              </button>
            );
          })}
          {reports.filter(r => !r.customerId).length > 0 && (
            <button onClick={() => setActiveCustomerId(0 as any)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap shrink-0 ${
                activeCustomerId === 0
                  ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                  : 'bg-slate-800/50 text-slate-400 border border-slate-800/40 hover:bg-slate-800 hover:text-slate-200'
              }`}
            >
              <AlertTriangle className="w-3 h-3" /> Unassigned
            </button>
          )}
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search reports, samples, clients…"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-800/60 border border-slate-800/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-rb-brand/40 transition-colors"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        <button
          onClick={() => setSortOrder(o => o === 'newest' ? 'oldest' : 'newest')}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 border border-slate-800/50 text-xs font-medium text-slate-400 hover:text-slate-200 transition-all"
        >
          {sortOrder === 'newest' ? <SortDesc className="w-3.5 h-3.5" /> : <SortAsc className="w-3.5 h-3.5" />}
          {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
        </button>
      </div>

      {/* ── Report List ── */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="rb-card p-4 animate-pulse">
              <div className="h-3 bg-slate-800 rounded w-1/3 mb-2" />
              <div className="h-3 bg-slate-800 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="rb-card p-12 text-center">
          <TrendingUp className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-400 mb-1">
            {search ? 'No matching reports' : 'No assessment reports yet'}
          </p>
          <p className="text-xs text-slate-600 max-w-md mx-auto">
            Run assessments from the main calculator and save them to generate trend reports.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredReports.map(report => {
            const status = getComplianceStatus(report.result);
            const activeDosing = (report.dosingConfig || []).filter((d: any) => d?.enabled).length;

            return (
              <motion.div key={report.id} layout
                className="rb-card border border-slate-800/40 hover:border-slate-600/60 transition-all relative"
              >
                {/* Delete confirm */}
                <AnimatePresence>
                  {deleteConfirm === report.id && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="absolute inset-0 z-10 bg-rb-elevated/95 rounded-xl flex items-center justify-center gap-3">
                      <span className="text-xs text-slate-300">Delete <strong className="text-white">{report.reportNumber}</strong>?</span>
                      <button onClick={() => handleDelete(report.id)} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25">Delete</button>
                      <button onClick={() => setDeleteConfirm(null)} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-800 text-slate-400 border border-slate-700">Cancel</button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center gap-3 px-4 py-3">
                  {/* Status strip */}
                  <div className={`w-1 self-stretch rounded-full shrink-0 ${
                    status.label === 'Compliant' ? 'bg-emerald-500' :
                    status.label === 'Review' ? 'bg-red-500' : 'bg-amber-500'
                  }`} />

                  {/* Icon */}
                  <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-slate-400" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-xs font-mono text-rb-brand font-bold">{report.reportNumber}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${status.bg} ${status.color} ${status.border}`}>
                        {status.label}
                      </span>
                      {report.fromServer && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold">
                          DB
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-white truncate">{report.sampleName}</p>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-[10px] text-slate-500 flex items-center gap-1">
                        <User className="w-2.5 h-2.5" />{report.customerName}
                      </span>
                      <span className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Calendar className="w-2.5 h-2.5" />{formatDate(report.date)}
                      </span>
                      {report.params && Object.keys(report.params).length > 0 && (
                        <span className="text-[10px] text-slate-500 flex items-center gap-1">
                          <BarChart3 className="w-2.5 h-2.5" />{Object.keys(report.params).length} params
                        </span>
                      )}
                      {activeDosing > 0 && (
                        <span className="text-[10px] text-cyan-400 flex items-center gap-1">
                          <FlaskConical className="w-2.5 h-2.5" />{activeDosing} chemical{activeDosing > 1 ? 's' : ''}
                        </span>
                      )}
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 font-mono">{report.standard}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => handlePreview(report)} title="Preview PDF"
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 hover:text-white transition-all">
                      <Eye className="w-3.5 h-3.5" /> Preview
                    </button>
                    <button onClick={() => handlePDF(report)} title="Download regulatory PDF"
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-rb-active text-white border border-rb-brand/25 hover:bg-rb-surface transition-all">
                      <Download className="w-3.5 h-3.5 text-rb-brand" /> PDF
                    </button>
                    <button onClick={() => setDeleteConfirm(report.id)} title="Delete"
                      className="p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors border border-transparent hover:border-red-500/20">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
          <p className="text-center text-[10px] text-slate-600 pt-1">
            Showing {filteredReports.length} of {reports.length} assessment report{reports.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}
